import { posKeyXYZ } from '../voxelMath'
import type { VoxelMap, Face } from '../voxelTypes'
import { normalToFace } from '../voxelLiterals'
import { Matrix3, Vector3, type CameraView } from '@/math'

// Determine an oriented face for a voxel (x,y,z) based on a seed normal and
// exposure to neighbors. Picks the exposed face most aligned with the seed.
// If no exposed faces, falls back to the seed normal.
export function computeVoxelFace(
  base: VoxelMap,
  x: number,
  y: number,
  z: number,
  bounds: { x: number; y: number; z: number },
  seedNormal: { x: number; y: number; z: number }
) {
  const seedVec = new Vector3(seedNormal.x, seedNormal.y, seedNormal.z).normalize()
  const dirs = [
    new Vector3(1,0,0), new Vector3(-1,0,0),
    new Vector3(0,1,0), new Vector3(0,-1,0),
    new Vector3(0,0,1), new Vector3(0,0,-1),
  ]
  let bestDot = -Infinity
  let bestDir: Vector3 | null = null
  for (const n of dirs) {
    const nx = x + Math.round(n.x)
    const ny = y + Math.round(n.y)
    const nz = z + Math.round(n.z)
    let exposed = true
    if (nx >= 0 && ny >= 0 && nz >= 0 && nx < bounds.x && ny < bounds.y && nz < bounds.z) {
      const nk = posKeyXYZ(nx, ny, nz)
      if (base.has(nk)) exposed = false
    }
    if (!exposed) continue
    const dot = n.dot(seedVec)
    if (dot > bestDot) { bestDot = dot; bestDir = n }
  }
  const chosen = bestDir || seedVec
  return normalToFace({ x: chosen.x, y: chosen.y, z: chosen.z })
}

const faceFromNormalLoose = (normal: { x: number; y: number; z: number }): Face => {
  const snapped = {
    x: Math.abs(normal.x) >= 0.5 ? Math.sign(normal.x) : 0,
    y: Math.abs(normal.y) >= 0.5 ? Math.sign(normal.y) : 0,
    z: Math.abs(normal.z) >= 0.5 ? Math.sign(normal.z) : 0,
  }
  const exact = normalToFace(snapped as any)
  if (exact) return exact
  const ax = Math.abs(normal.x)
  const ay = Math.abs(normal.y)
  const az = Math.abs(normal.z)
  if (ax >= ay && ax >= az) return normal.x >= 0 ? 'x+' : 'x-'
  if (ay >= ax && ay >= az) return normal.y >= 0 ? 'y+' : 'y-'
  return normal.z >= 0 ? 'z+' : 'z-'
}

const oppositeFace = (face: Face): Face => {
  switch (face) {
    case 'x+': return 'x-'
    case 'x-': return 'x+'
    case 'y+': return 'y-'
    case 'y-': return 'y+'
    case 'z+': return 'z-'
    case 'z-': return 'z+'
  }
}

// Given a surface face and camera, choose the in-plane axis most aligned with screen up.
export function computeTopFaceOnSurface(face: Face, camera: CameraView): Face {
  // Tangent axes per face (world-aligned)
  const tangents: Array<{ vec: Vector3; face: Face }> = (() => {
    switch (face) {
      case 'y+':
      case 'y-':
        return [
          { vec: new Vector3(1, 0, 0), face: 'x+' },
          { vec: new Vector3(0, 0, 1), face: 'z+' },
        ]
      case 'x+':
      case 'x-':
        return [
          { vec: new Vector3(0, 1, 0), face: 'y+' },
          { vec: new Vector3(0, 0, 1), face: 'z+' },
        ]
      case 'z+':
      case 'z-':
        return [
          { vec: new Vector3(1, 0, 0), face: 'x+' },
          { vec: new Vector3(0, 1, 0), face: 'y+' },
        ]
    }
  })()

  const normalMatrix = new Matrix3().getNormalMatrix(camera.matrixWorldInverse)

  let bestFace: Face = tangents[0]!.face
  let bestScore = -Infinity
  for (const t of tangents) {
    const view = t.vec.clone().applyMatrix3(normalMatrix)
    const s = view.y
    const absScore = Math.abs(s)
    if (absScore > bestScore) {
      bestScore = absScore
      bestFace = s >= 0 ? t.face : oppositeFace(t.face)
    }
  }
  return bestFace
}

export function topFaceForNormal(normal: { x: number; y: number; z: number } | undefined, camera: CameraView): Face | undefined {
  if (!normal) return undefined
  const face = faceFromNormalLoose(normal)
  return computeTopFaceOnSurface(face, camera)
}
